import { Axios } from "axios";
import JWTUtil from "jsonwebtoken";
import env from "../env.js";

export class Api extends Axios {
  constructor() {
    super({
      baseURL: env.apiUrl,
      headers: {
        // "Access-Control-Allow-Origin": "*",
        "Content-Type": "application/json"
      },
      transformRequest: [data => JSON.stringify(data)],
      transformResponse: [
        data => {
          try {
            return JSON.parse(data);
          } catch (e) {
            return data;
          }
        }
      ]
    });

    this.jwt = null;

    this.setupWrappers();

    this.interceptors.response.use(
      response => {
        if (response && response.status === 401) {
          window.location.href = "/login";
        }
        return response;
      },
      error => {
        if (error && error.response && error.response.status === 401) {
          window.location.href = "/login";
        }
        return error;
      }
    );
  }

  setupWrappers() {
    // User wrapper
    this.pricing = {
      get: () => {
        return this.get("/pricing");
      }
    };

    this.register = data => {
      return this.post("/signup", data);
    };

    this.users = {
      get: (id = false) => {
        return this.get("/users" + (id ? "/" + id : ""));
      },
      self: () => {
        if (!this.isTokenValid()) return new RefectPromise("Not authenticated");
        return this.get("/users/" + this.getUserID());
      },
      update: (data, id = this.getUserID()) => {
        return this.patch("/users/" + id, data);
      },
      delete: id => {
        return this.delete("/users/" + id);
      },
      password: (data, id = this.getUserID()) => {
        return this.put("/users/" + id + "/password", data);
      },
      profile: (data, id = this.getUserID()) => {
        return this.patch("/users/" + id + "/profile", data);
      }
    };
    // Server wrapper
    this.servers = {
      get: (id = false) => {
        return this.get("/servers" + (id ? "/" + id : ""));
      },
      create: data => {
        return this.post("/servers", data);
      },
      update: (id, data) => {
        return this.patch("/servers/" + id, data);
      },
      delete: id => {
        return this.delete("/servers/" + id);
      },
      start: id => {
        return this.post("/servers/" + id + "/instance");
      },
      stop: id => {
        return this.delete("/servers/" + id + "/instance");
      }
    };
    // Usage wrapper
    this.usages = {
      summary: {
        ranges: () => {
          return this.get("/usages/summary/ranges");
        },
        get: (from, to) => {
          return this.get("/usages/summary?from=" + from + "&to=" + to);
        }
      }
      /*get: (id = false) => {
        return this.get("/servers" + (id ? "/" + id : ""));
      },
      create: data => {
        return this.post("/servers", data);
      },
      update: (id, data) => {
        return this.patch("/servers/" + id, data);
      },
      delete: id => {
        return this.delete("/servers/" + id);
      },
      start: id => {
        return this.post("/servers/" + id + "/instance");
      },
      stop: id => {
        return this.delete("/servers/" + id + "/instance");
      }*/
    };
    /*    this.dashboard = {
      users: {
        get: (id = false) => {
          return this.get("/dashboard/users" + (id ? "/" + id : ""));
        },
        self: () => {
          if (!this.isTokenValid())
            return new RefectPromise("Not authenticated");
          return this.get("/dashboard/users/" + this.getUserID());
        },
        put: (data, id = this.getUserID()) => {
          return this.put("/dashboard/users/" + id, data);
        },
        delete: id => {
          return this.delete("/dashboard/users/" + id);
        },
        password: (data, id = this.getUserID()) => {
          return this.put("/dashboard/users/" + id + "/password", data);
        }
      },
      apiKeys: {
        get: (id = false) => {
          return this.get("/dashboard/api-keys" + (id ? "/" + id : ""));
        }
      }
    };

    this.partners = {
      get: (id = false) => {
        return this.get("/partners" + (id ? "/" + id : ""));
      },
      detailed: id => {
        return this.get("/partners/" + id + "/detailed");
      },
      list: (page = 1, search = false) => {
        if (page === "all") return this.get("/partners?all=1");

        return this.get(
          "/partners?page=" + page + (search ? "&search=" + search : "")
        );
      },
      post: data => {
        return this.post("/partners", data);
      },
      subscriptions: {
        post: (partner, data) => {
          return this.post(
            "/partners/" + (partner ? partner : "*") + "/subscriptions",
            data
          );
        },
        list: (partner = "", page = 1, search = false) => {
          return this.get(
            "/partners/" +
              (partner ? partner : "*") +
              "/subscriptions?page=" +
              page +
              (search ? "&search=" + search : "")
          );
        }
      }
    };

    this.customers = {
      get: (id = false) => {
        return this.get("/customers" + (id ? "/" + id : ""));
      },
      detailed: id => {
        return this.get("/customers/" + id + "/detailed");
      },
      list: (page = 1, search = false) => {
        return this.get(
          "/customers?page=" + page + (search ? "&search=" + search : "")
        );
      },
      stats: () => {
        return this.get("/customers/statistics");
      }
    };

    this.documents = {
      get: (id = false) => {
        return this.get("/documents" + (id ? "/" + id : ""));
      },
      list: (page = 1, paginate = 15, search = false) => {
        return this.get(
          "/documents?page=" +
            page +
            (search ? "&search=" + search : "") +
            (paginate ? "&paginate=" + paginate : "")
        );
      },
      download: id => {
        return this.get("/documents/" + id + "/download");
      }
    };

    this.subscriptions = {
      get: (id = false) => {
        return this.get("/subscriptions" + (id ? "/" + id : ""));
      },
      list: (page = 1, search = false) => {
        return this.get(
          "/subscriptions?page=" + page + (search ? "&search=" + search : "")
        );
      },
      status: (id, status) => {
        return this.put("/subscriptions/" + id, { status: status });
      }
    };

    this.pixel = {
      identities: {
        get: (id = false) => {
          return this.get("/pixel/identities" + (id ? "/" + id : ""));
        },
        list: (page = 1, search = false) => {
          return this.get(
            "/pixel/identities?page=" +
              page +
              (search ? "&search=" + search : "")
          );
        }
      },
      logs: {
        get: (id = false) => {
          return this.get("/pixel/identities" + (id ? "/" + id : ""));
        },
        list: (page = 1, search = false) => {
          return this.get(
            "/pixel/identities/all/logs?page=" +
              page +
              (search ? "&search=" + search : "")
          );
        }
      },
      profiles: {
        get: id => {
          return this.get("/pixel/identities/" + id + "/profiles");
        },
        list: (page = 1, search = false) => {
          return this.get(
            "/pixel/identities/list/profiles?page=" +
              page +
              (search ? "&search=" + search : "")
          );
        }
      }
    };

    this.lernio = {
      users: {
        get: (id = false) => {
          return this.get("/lernio/users" + (id ? "/" + id : ""));
        },
        list: (page = 1, search = false) => {
          return this.get(
            "/lernio/users?page=" + page + (search ? "&search=" + search : "")
          );
        }
      }
    };

    this.controlCenter = {
      statistics: {
        get: scope => {
          return this.get("/control-center/statistics/" + scope);
        }
      },
      ccstats: {
        get: () => {
          return this.get("/control-center/ccstats");
        }
      },
      job: {
        create: (controller, data) => {
          return this.post("/control-center/job", {
            controller: controller,
            status: "ready",
            data: data
          });
        }
      }
    };*/
  }

  getToken(email, password) {
    return this.post("/token", {
      email: email,
      password: password
    });
  }

  setupWithToken(token) {
    if (!token) {
      console.log("[AUTH] TOKEN_VALID", this.isTokenValid());
      return;
    }
    this.jwt = JWTUtil.decode(token);
    this.defaults.headers["Authorization"] = "Bearer " + token;
    console.log("[AUTH] TOKEN_VALID", this.isTokenValid());
  }

  destroy() {
    this.defaults.headers["Authorization"] = "Bearer 0";
    this.jwt = null;
  }

  getUserID() {
    return (this.isTokenValid() && this.jwt.sub) || null;
  }

  isTokenValid() {
    return !!(this.jwt && (this.jwt.exp - 30) * 1000 > Date.now());
  }
}

class RefectPromise {
  constructor(rejectMessage) {
    return new Promise((resolve, reject) => {
      reject(rejectMessage);
    });
  }
}
